import React, { useState, useEffect, useContext } from "react";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Grid } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    backgroundColor: theme.mode === "dark" ? "#1f2937" : theme.palette.background.paper,
  },

  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#eeeeee",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === "dark" ? "#374151" : "white",
        boxShadow: `0 0 0 2px ${theme.mode === "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(25, 118, 210, 0.2)"}`,
      },
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  // ✅ ESTILOS DE BOTONES MEJORADOS
  primaryButton: {
    backgroundColor: "#1976d2",
    color: "white",
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },

  secondaryButton: {
    color: "#1976d2",
    borderColor: "#1976d2",
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.1)",
      borderColor: "#1565c0",
    },
  },

  // ✅ CORREGIDO: DialogTitle dinámico según tema
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    padding: theme.spacing(2),
  },

  // ✅ CORREGIDO: DialogContent dinámico según tema
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#fafafa",
  },

  // ✅ CORREGIDO: DialogActions dinámico según tema
  dialogActions: {
    padding: theme.spacing(2),
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f5f5f5",
    borderTop: `1px solid ${theme.mode === "dark" ? "#4B5563" : theme.palette.divider}`,
    justifyContent: "space-between",
  },

  // ✅ CORREGIDO: SectionTitle dinámico según tema
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
  },
}));

const ColumnAliasModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const initialState = {
    "Columna 4": "Columna 4",
    "Columna 5": "Columna 5",
    "Columna 6": "Columna 6",
    "Columna 7": "Columna 7",
    "Columna 8": "Columna 8",
    "Columna 9": "Columna 9",
    "Columna 10": "Columna 10",
  };

  const [aliases, setAliases] = useState(initialState);
  const companyId = user?.companyId;

  useEffect(() => {
    const loadAliases = async () => {
      if (!open || !companyId) return;

      setLoading(true);
      try {
        const { data } = await api.get("/settings");

        // Filtrar solo los settings de columnas (Columna 4 a Columna 10)
        const columnKeys = [
          "Columna 4",
          "Columna 5",
          "Columna 6",
          "Columna 7",
          "Columna 8",
          "Columna 9",
          "Columna 10",
        ];
        const columnSettings = data.filter((setting) => columnKeys.includes(setting.key));

        // Crear objeto con los alias (usar value si existe, sino usar key como fallback)
        const newAliases = {};
        columnKeys.forEach((key) => {
          const setting = columnSettings.find((s) => s.key === key);
          newAliases[key] = setting?.value || key;
        });

        setAliases(newAliases);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    loadAliases();
  }, [open, companyId]);

  const handleClose = () => {
    onClose();
    setAliases(initialState);
  };

  const handleSaveAliases = async (values) => {
    try {
      setLoading(true);
      const columnKeys = [
        "Columna 4",
        "Columna 5",
        "Columna 6",
        "Columna 7",
        "Columna 8",
        "Columna 9",
        "Columna 10",
      ];

      // Actualizar cada setting individualmente
      const updatePromises = columnKeys.map((key) => {
        const value = values[key] || key;
        return api.put(`/settings/${key}`, { value });
      });

      await Promise.all(updatePromises);
      toast.success("Alias de columnas guardados exitosamente");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" scroll="paper" fullWidth>
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          <SettingsIcon fontSize="small" style={{ marginRight: 8, verticalAlign: "middle" }} />
          Campos Opcionales
        </DialogTitle>
        <Formik
          initialValues={aliases}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveAliases(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {loading ? (
                  <div style={{ textAlign: "center", padding: theme.spacing(4) }}>
                    <CircularProgress />
                    <Typography variant="body2" style={{ marginTop: theme.spacing(2) }}>
                      Cargando configuración...
                    </Typography>
                  </div>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom className={classes.sectionTitle}>
                      Personalizar Nombres de Columnas
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        marginBottom: theme.spacing(2),
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Define nombres personalizados para las columnas adicionales. Estos nombres se
                      mostrarán en toda la aplicación.
                    </Typography>
                    <Grid spacing={2} container>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 4"
                          name="Columna 4"
                          value={values["Columna 4"] || ""}
                          onChange={(e) => setFieldValue("Columna 4", e.target.value)}
                          placeholder="Ej: DNI, Nro Boleta, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 5"
                          name="Columna 5"
                          value={values["Columna 5"] || ""}
                          onChange={(e) => setFieldValue("Columna 5", e.target.value)}
                          placeholder="Ej: Fecha Vencimiento, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 6"
                          name="Columna 6"
                          value={values["Columna 6"] || ""}
                          onChange={(e) => setFieldValue("Columna 6", e.target.value)}
                          placeholder="Ej: Monto, Importe, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 7"
                          name="Columna 7"
                          value={values["Columna 7"] || ""}
                          onChange={(e) => setFieldValue("Columna 7", e.target.value)}
                          placeholder="Ej: Estado, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 8"
                          name="Columna 8"
                          value={values["Columna 8"] || ""}
                          onChange={(e) => setFieldValue("Columna 8", e.target.value)}
                          placeholder="Ej: Prioridad, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 9"
                          name="Columna 9"
                          value={values["Columna 9"] || ""}
                          onChange={(e) => setFieldValue("Columna 9", e.target.value)}
                          placeholder="Ej: Observaciones, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <TextField
                          label="Columna 10"
                          name="Columna 10"
                          value={values["Columna 10"] || ""}
                          onChange={(e) => setFieldValue("Columna 10", e.target.value)}
                          placeholder="Ej: Notas, etc."
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting || loading}
                  variant="outlined"
                  className={classes.secondaryButton}
                >
                  CANCELAR
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || loading}
                  variant="contained"
                  className={`${classes.btnWrapper} ${classes.primaryButton}`}
                >
                  GUARDAR
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ColumnAliasModal;
